import React, {useCallback, useEffect, useState} from "react";
import {Chip, CircularProgress, Typography} from "@mui/material";
import {useNavigate} from "react-router";

import PageHeader from "../components/Common/PageHeader";
import PersonaHeaderInfo from "../components/Common/PersonaHeaderInfo";
import {TrainingMetrics as Metrics, getTrainingMetrics} from "../data/training";
import {formatLocal} from "../util/Formatters";
import {useSessionContext} from "../util/SessionContext";
import {Blue3, Grey2, Grey5, Grey6, Grey17, White} from "../theme/styles";

// What the organization owes, and what it has completed.
//
// A WORKLIST WITH NUMBERS ON IT, NOT A CHART PAGE. "3 overdue (30%)" tells somebody a percentage
// and nothing to do about it; "Jenny Hartwell -- SOP-01011 -- 40 days" is a task they can pick up.
// So the overdue table IS this page, and everything above it is context for reading it.
//
// ONE PERCENTAGE, AT THE TOP, WITH ITS DENOMINATOR. Enlil's version puts three stacked bars side by
// side where the middle one's denominator is the INCOMPLETE subset and its neighbour's is every
// training -- two cards, two denominators, adjacent. In a compliance context that is an invitation
// to misread, and misreading is the whole risk.
//
// IT SAYS WHAT IT CANNOT KNOW. Obligations are computed, never stored, so this reports what is owed
// NOW and what has been completed whenever -- not what was owed last March and abandoned. A page
// that quietly omitted that would be read as a full compliance history, which it is not.

const OVERDUE = "#B42318";
const WARN = "#B54708";
const DONE = "#067647";

function Stat({value, label, tint, ink}: {value: number; label: string; tint: string; ink: string}) {
    return (
        <div style={{flex: 1, minWidth: 150, padding: "16px 20px", backgroundColor: White,
                     border: `1px solid ${Grey2}`, borderRadius: 12, boxSizing: "border-box"}}>
            <Typography style={{fontSize: 26, fontWeight: 700, color: ink, lineHeight: 1.15}}>
                {value}
            </Typography>
            <Typography style={{fontSize: 12.5, color: Grey6}}>{label}</Typography>
            <div style={{height: 3, borderRadius: 2, backgroundColor: tint, marginTop: 8}}/>
        </div>
    );
}

export default function TrainingMetrics() {
    const sessionContext = useSessionContext();
    const navigate = useNavigate();
    const [metrics, setMetrics] = useState<Metrics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(() => {
        setLoading(true);
        getTrainingMetrics(sessionContext)
            .then(result => { setMetrics(result); setError(null); })
            .catch(problem => setError(problem?.response?.data?.Cause
                ?? "Could not read the training metrics."))
            .finally(() => setLoading(false));
    }, [sessionContext]);

    useEffect(load, [load]);

    return (
        <div style={{display: "flex", flexDirection: "column", flexGrow: 1, minHeight: "100%"}}>
            <PageHeader title="Training Metrics">
                <PersonaHeaderInfo/>
            </PageHeader>

            <div style={{padding: "20px 30px 48px"}}>
                {loading ? <CircularProgress size={22}/> : error ? (
                    <Typography style={{fontSize: 13, color: OVERDUE}}>{error}</Typography>
                ) : !metrics || metrics.Scope === null ? (
                    <Typography style={{fontSize: 14, color: Grey5}}>
                        Your active persona resolves to no organization, so there is nothing to report.
                    </Typography>
                ) : (
                    <>
                        {/* THE SENTENCE AN AUDITOR OPENS WITH. One line, with its denominator. */}
                        <Typography style={{fontSize: 15, color: Grey17, marginBottom: 16}}>
                            {metrics.Owed === 0
                                ? "Nothing is currently owed in your organization."
                                : <>
                                    <b>{metrics.Owed}</b> trainings owed across{" "}
                                    <b>{metrics.People}</b> {metrics.People === 1 ? "person" : "people"}.{" "}
                                    <b>{metrics.Complete}</b> complete,{" "}
                                    <b>{metrics.Outstanding}</b> outstanding,{" "}
                                    <b style={{color: metrics.Overdue ? OVERDUE : Grey17}}>
                                        {metrics.Overdue}
                                    </b> overdue.
                                  </>}
                        </Typography>

                        <div style={{display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 24}}>
                            <Stat value={metrics.Complete} label="Complete" tint={DONE} ink={Grey17}/>
                            <Stat value={metrics.Outstanding} label="Outstanding" tint={WARN} ink={Grey17}/>
                            <Stat value={metrics.Overdue} label="Overdue" tint={OVERDUE}
                                  ink={metrics.Overdue ? OVERDUE : Grey17}/>
                            <Stat value={metrics.People} label="People" tint={Blue3} ink={Grey17}/>
                        </div>

                        {/* ── the worklist ───────────────────────────────────────────────────── */}
                        <Heading>Overdue</Heading>
                        {metrics.OverdueRows.length === 0 ? (
                            <Typography style={{fontSize: 13.5, color: Grey5, marginBottom: 26}}>
                                Nobody is overdue.
                            </Typography>
                        ) : (
                            <div style={{border: `1px solid ${Grey2}`, borderRadius: 10,
                                         marginBottom: 26, overflow: "hidden"}}>
                                {metrics.OverdueRows.map((row, index) => (
                                    <div key={`${row.AccountUserUSID}-${row.ObjectRevisionUSID}`}
                                         style={{display: "flex", alignItems: "center", gap: 12,
                                                 padding: "11px 16px", backgroundColor: White,
                                                 borderTop: index ? `1px solid ${Grey2}` : undefined}}>
                                        <Typography style={{fontSize: 13.5, color: Grey17, minWidth: 190,
                                                            whiteSpace: "nowrap", overflow: "hidden",
                                                            textOverflow: "ellipsis"}}>
                                            {row.Who ?? "Unnamed person"}
                                        </Typography>
                                        <Typography onClick={() => navigate(`/documents/${row.ObjectRevisionUSID}`)}
                                                    style={{fontSize: 13.5, fontWeight: 600, color: Blue3,
                                                            whiteSpace: "nowrap", cursor: "pointer",
                                                            flexShrink: 0}}>
                                            {row.ObjectID ?? "—"}
                                        </Typography>
                                        <Typography title={row.Name ?? ""}
                                                    style={{fontSize: 13.5, color: Grey17, flex: 1,
                                                            minWidth: 0, whiteSpace: "nowrap",
                                                            overflow: "hidden", textOverflow: "ellipsis"}}>
                                            {row.Name}
                                        </Typography>
                                        {row.CurriculumObjectID ? (
                                            <Chip size="small" variant="outlined"
                                                  label={row.CurriculumObjectID}
                                                  sx={{fontSize: 11, height: 20, flexShrink: 0}}/>
                                        ) : null}
                                        <Typography style={{fontSize: 12, color: Grey6, flexShrink: 0,
                                                            whiteSpace: "nowrap"}}>
                                            due {formatLocal(row.DueDatetime)}
                                        </Typography>
                                        <div style={{padding: "3px 10px", borderRadius: 14, fontSize: 12,
                                                     fontWeight: 700, color: White, flexShrink: 0,
                                                     backgroundColor: "#D92D20", whiteSpace: "nowrap"}}>
                                            {row.DaysOverdue}d
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* ── the bottleneck view ────────────────────────────────────────────── */}
                        <Heading>By record</Heading>
                        <Typography style={{fontSize: 12.5, color: Grey5, marginBottom: 10, maxWidth: "80ch"}}>
                            One document nobody has read is a different problem from the same number of
                            people behind on different things. Sorted by what is blocking most.
                        </Typography>
                        {metrics.ByRecord.length === 0 ? (
                            <Typography style={{fontSize: 13.5, color: Grey5}}>
                                No record is owed by anybody.
                            </Typography>
                        ) : (
                            <div style={{border: `1px solid ${Grey2}`, borderRadius: 10, overflow: "hidden"}}>
                                {metrics.ByRecord.map((row, index) => (
                                    <div key={`${row.ObjectID}-${index}`}
                                         style={{display: "flex", alignItems: "center", gap: 12,
                                                 padding: "11px 16px", backgroundColor: White,
                                                 borderTop: index ? `1px solid ${Grey2}` : undefined}}>
                                        <Typography style={{fontSize: 13.5, fontWeight: 600, color: Blue3,
                                                            whiteSpace: "nowrap", flexShrink: 0}}>
                                            {row.ObjectID ?? "—"}
                                        </Typography>
                                        <Typography title={row.Name ?? ""}
                                                    style={{fontSize: 13.5, color: Grey17, flex: 1,
                                                            minWidth: 0, whiteSpace: "nowrap",
                                                            overflow: "hidden", textOverflow: "ellipsis"}}>
                                            {row.Name}
                                            {row.DisplayRevision ? (
                                                <span style={{color: Grey5}}> · Rev {row.DisplayRevision}</span>
                                            ) : null}
                                        </Typography>
                                        <Typography style={{fontSize: 12, color: Grey6, flexShrink: 0,
                                                            whiteSpace: "nowrap"}}>
                                            {row.Complete}/{row.Owed} done
                                        </Typography>
                                        {row.Overdue > 0 ? (
                                            <Typography style={{fontSize: 12, fontWeight: 700, color: OVERDUE,
                                                                flexShrink: 0, whiteSpace: "nowrap"}}>
                                                {row.Overdue} overdue
                                            </Typography>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* SAID ON THE PAGE, not only in the docs. Somebody reading a compliance
                            screen will assume it covers history unless it says otherwise. */}
                        <Typography style={{fontSize: 12, color: Grey6, marginTop: 22, maxWidth: "80ch"}}>
                            These figures are what is owed <b>now</b>. Training obligations are worked out
                            live from the curricula in force, so this cannot report what somebody was
                            overdue on in the past and never completed — completed training keeps its own
                            record and its deadline.
                        </Typography>
                    </>
                )}
            </div>
        </div>
    );
}

function Heading({children}: {children: React.ReactNode}) {
    return (
        <Typography style={{fontSize: 12, fontWeight: 700, color: Grey6, letterSpacing: "0.06em",
                            textTransform: "uppercase", margin: "4px 0 8px"}}>
            {children}
        </Typography>
    );
}
